import { FC, ReactNode, useState } from 'react';
import { Col, Dropdown, Menu, Row } from 'antd';
import {
    TbArrowUpRight,
    TbBook2,
    TbCalendarShare,
    TbFileDescription,
    TbGift,
    TbInfoCircle,
    TbLifebuoy,
    TbSpeakerphone,
    TbWorld,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useLatestRelease } from '@/hooks/useLatestRelease';
import { MiddleDot, openNotionFormModal } from '@/shared';
import { DropdownMenuPanel } from '@/shared/components/dropdowns/DropdownPanel';
import { formatDate } from '@/utilities';

interface HelpButtonDropdownProps {
    children: ReactNode;
}

const HelpButtonDropdown: FC<HelpButtonDropdownProps> = ({ children }): JSX.Element => {
    const [loading, setLoading] = useState(true);

    const { latestRelease } = useLatestRelease();

    const { token } = useAntDesignTheme();

    return (
        <Dropdown
            trigger={['click']}
            placement='topRight'
            popupRender={() => (
                <DropdownMenuPanel
                    style={{
                        width: 16 * 18,
                        marginBottom: '0.75rem',
                        border: `1px solid ${token.colorBorder}`,
                        paddingBottom: 0,
                    }}
                >
                    <small style={{ marginLeft: '0.75rem', color: token.colorTextTertiary }}>
                        <TbInfoCircle
                            style={{
                                verticalAlign: '-0.1rem',
                                marginRight: '0.25rem',
                            }}
                        />
                        Help
                    </small>
                    <Menu.Divider />
                    <Menu.Item
                        key='enterpriseDemo'
                        icon={
                            <TbCalendarShare
                                size={'1rem'}
                                style={{ verticalAlign: '-0.2rem', marginRight: '0.375rem' }}
                            />
                        }
                        onClick={() => {
                            window.open('https://cal.com/countercyclical/enterprise-demo', '_blank');
                        }}
                    >
                        Schedule an Enterprise Demo
                        <TbArrowUpRight
                            style={{
                                verticalAlign: '-0.15rem',
                                marginLeft: '0.375rem',
                                color: token.colorTextTertiary,
                            }}
                        />
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Item
                        key='website'
                        icon={<TbWorld size={'1rem'} style={{ verticalAlign: '-0.2rem', marginRight: '0.375rem' }} />}
                        onClick={() => {
                            window.open('https://countercyclical.io', '_blank');
                        }}
                    >
                        Website
                        <TbArrowUpRight
                            style={{
                                verticalAlign: '-0.15rem',
                                marginLeft: '0.375rem',
                                color: token.colorTextTertiary,
                            }}
                        />
                    </Menu.Item>
                    <Menu.Item
                        key='documentation'
                        icon={<TbBook2 size={'1rem'} style={{ verticalAlign: '-0.2rem', marginRight: '0.375rem' }} />}
                        onClick={() => {
                            window.open('https://docs.countercyclical.io', '_blank');
                        }}
                    >
                        Documentation
                        <TbArrowUpRight
                            style={{
                                verticalAlign: '-0.15rem',
                                marginLeft: '0.375rem',
                                color: token.colorTextTertiary,
                            }}
                        />
                    </Menu.Item>
                    <Menu.Item
                        key='changelog'
                        icon={<TbGift size={'1rem'} style={{ verticalAlign: '-0.2rem', marginRight: '0.375rem' }} />}
                        onClick={() => {
                            window.open('https://countercyclical.canny.io/changelog', '_blank');
                        }}
                    >
                        What's New
                        <TbArrowUpRight
                            style={{
                                verticalAlign: '-0.15rem',
                                marginLeft: '0.375rem',
                                color: token.colorTextTertiary,
                            }}
                        />
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Item
                        key='privacy'
                        icon={<TbFileDescription size={'1rem'} />}
                        onClick={() => {
                            window.open('https://countercyclical.io/privacy', '_blank');
                        }}
                    >
                        Privacy
                        <TbArrowUpRight
                            style={{
                                verticalAlign: '-0.15rem',
                                marginLeft: '0.375rem',
                                color: token.colorTextTertiary,
                            }}
                        />
                    </Menu.Item>
                    <Menu.Item
                        key='tos'
                        icon={<TbFileDescription size={'1rem'} />}
                        onClick={() => {
                            window.open('https://countercyclical.io/tos', '_blank');
                        }}
                    >
                        Terms of Service
                        <TbArrowUpRight
                            style={{
                                verticalAlign: '-0.15rem',
                                marginLeft: '0.375rem',
                                color: token.colorTextTertiary,
                            }}
                        />
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Item
                        key='whatsNew'
                        icon={
                            <TbSpeakerphone
                                size={'1rem'}
                                style={{ verticalAlign: '-0.2rem', marginRight: '0.375rem' }}
                            />
                        }
                        onClick={() => openNotionFormModal('feedback')}
                    >
                        Give Us Feedback
                    </Menu.Item>
                    <Menu.Item
                        key='support'
                        icon={
                            <TbLifebuoy size={'1rem'} style={{ verticalAlign: '-0.2rem', marginRight: '0.375rem' }} />
                        }
                        onClick={() => openNotionFormModal('support')}
                    >
                        Support
                    </Menu.Item>
                    <Menu.Divider style={{ marginBottom: 0 }} />
                    <Row
                        style={{
                            color: token.colorTextTertiary,
                            backgroundColor: token.colorBgBase,
                            paddingTop: '0.125rem',
                            paddingBottom: '0.25rem',
                        }}
                        align={'middle'}
                    >
                        <Col flex='none'>
                            <small style={{ marginLeft: '0.75rem' }}>
                                {latestRelease?.tag_name && latestRelease?.tag_name}
                                <MiddleDot horizontalMargin={'0.5rem'} />
                                Released{' '}
                                {latestRelease?.created_at &&
                                    latestRelease?.created_at &&
                                    formatDate(latestRelease?.created_at)}
                            </small>
                        </Col>
                    </Row>
                </DropdownMenuPanel>
            )}
        >
            {children}
        </Dropdown>
    );
};

export default HelpButtonDropdown;
